import { computed, inject, resource, signal } from '@angular/core';
import { patchState, signalStore, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { withEntities } from '@ngrx/signals/entities';
import { Router } from '@angular/router';
import { ProjectService } from '../services/project.service';
import { EditProjectServicePayload, Invitations, InviteUserPayload, isAdmin, Project, ProjectEnv, ProjectEnvironmentPayload, ProjectInvite, ProjectServicePayload, ProjectServices, ProjectUser, Repository, RepositoryPayload,  } from '../models/project.model';
import { combineLatest, firstValueFrom, map } from 'rxjs';
import { userStore } from './user.store';
import { EruqlService } from '../services/eruql.service';
import { ErufileService } from '../services/erufile.service';
import { EruaiService } from '../services/eruai.service';
import { ErufunctionService } from '../services/erufunctions.service';
import { EruAuthService } from '../services/eruauth.service';
import { EruGatewayService } from '../services/erugateway.service';
import { ConfigService, InstanceAuth, instanceAuthHeaders } from '../services/config.service';
import { InstanceAuthRegistry } from '../services/auth.interceptor';
import { SnackbarService } from '../services/snackbar.service';
import { RuleTypePayload } from '../components/erugateway/erugateway-list-form/erugateway-list-form';
import { Authorizer } from '../models/erugateway.model';
import { DataSources } from '../models/eruql.model';
import { Storages, AESKey } from '../models/erufiles.model';
import { Auth } from '../models/eruauth.model';
import { ServiceConfig } from '../models/service-config.model';
import { User } from '../models/user.model';
import { userInfo } from '../models/user.model';

export type ComparisonRequest = {
  serviceName: string;
  projectName: string;
  clickedEnv: string;
  clickedServicePath: string;
  selectedEnv: string;
  selectedServicePath: string;
};

type ProjectState = {
  projects: Project[] | undefined;
  requestParams: { project_name?: string; env_name?: string };
  user_invites: {
    project_invite: ProjectInvite[] | undefined;
    invites: Invitations[] | undefined;
  };
  repositories: Repository[] | undefined;
  selectedRepository: Repository | null;
  selectedEnvId: string | null;
  comparisonData: ServiceConfig | null;
  comparisonRequest: ComparisonRequest | null;
  comparisonResult: any | null;
  comparisonTargetData: any | null;
  currentUser: User | null;
  userId?: string; // Added userId property
  loading?: boolean; // Added loading property
  projectUsers: ProjectUser[] | null; 
};
const initialState: ProjectState = {
  projects: undefined,
  requestParams: { project_name: undefined, env_name: undefined },
  user_invites: { project_invite: undefined, invites: undefined },
  repositories: undefined,
  selectedRepository: null,
  selectedEnvId: null,
  comparisonData: null,
  comparisonRequest: {
    serviceName: '',
    projectName: '',
    clickedEnv: '',
    clickedServicePath: '',
    selectedEnv: '',
    selectedServicePath: '',
  },
  comparisonResult: null,
  comparisonTargetData: null,
  currentUser: null,
  loading: false, // Added loading property
  projectUsers: null
};


export const projectStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withEntities<Project>(),
  withProps((store) => ({
    _projectService: inject(ProjectService),
    _router: inject(Router),
    _userStore: inject(userStore),
    _eruqlService: inject(EruqlService),
    _erufileService: inject(ErufileService),
    _eruaiService: inject(EruaiService),
    _erufunctionService: inject(ErufunctionService),
    _eruauthService: inject(EruAuthService),
    _erugatewayService: inject(EruGatewayService),
    _configService: inject(ConfigService),
    _snackbarService: inject(SnackbarService),
    _instanceAuthRegistry: inject(InstanceAuthRegistry),
    // env_id -> instance auth obtained for compare/import, with the time it was obtained
    _instanceAuthCache: new Map<string, { auth: InstanceAuth | null; at: number }>(),
    // in-flight fetchProjects request, shared with projectsResource so it does not refetch
    _projectsFetch: { current: null as Promise<Project[]> | null },
  })),
  withProps((store) => ({
    projectCount: computed(() => store.projects()?.length || 0),
    projectList: computed(() =>
      processProjects(structuredClone(store.projects() ?? []))
    ),
    getEnvironmentAccess: computed(() => (projectId: string, envId: string) => {
      const project = store.projects()?.find((p) => p.project_id === projectId);
      const env = project?.public___project_envs?.find((e) => e.env_id === envId);
      return env?.service_access || 'READ'; // Default to READ
    }),
    getProjectAccess: computed(() => (projectId: string) => {
      const project = store.projects()?.find((p) => p.project_id === projectId);
      return project?.service_access || 'READ'; // Default to READ
    }),
    total_project: computed(() => store.projects() ?? []),
    user_invite_details: computed(() => store.user_invites() || undefined),
    repositoryList: computed(() => store.repositories() ?? []),
    selectedRepositoryDetails: computed(() => store.selectedRepository()),
    // Distinct users across all projects the current user can see
    projectUserCount: computed(() => {
      const emails = new Set<string>();
      store.user_invites().project_invite?.forEach((invite: any) =>
        invite?.user_details?.forEach((user: any) => {
          if (user?.user_email) emails.add(user.user_email);
        })
      );
      return emails.size;
    }),
    getProjectEnvironments: computed(() => (projectId: string) => {
      const project = store.projects()?.find((p) => p.project_id === projectId);
      return project?.public___project_envs || [];
    }),
    getEnvironmentServices: computed(() => (projectId: string, envId: string) => {
      const project = store.projects()?.find((p) => p.project_id === projectId);
      const env = project?.public___project_envs?.find((e) => e.env_id === envId);
      return env?.public___project_services?.filter((s) => s.service_name) || [];
    }),
    // Mirrors fetchProjects (which owns the request) so consumers get status/isLoading
    projectsResource: resource<
      Project[],
      { project_name?: string; env_name?: string }
    >({
      params: () => store.requestParams(),
      loader: async () => {
        try {
          return (await store._projectsFetch.current) ?? store.projects() ?? [];
        } catch {
          return [];
        }
      },
    }),
    userResource: resource<void, { email: string }>({
      params: () => ({ email: store._userStore.user_details()?.email ?? '' }),
      loader: async ({ params }) => {
        if (params.email && window.location.href.includes('project')) {
          const invites = store._projectService.get_user_invites(params.email);
          const projectUsers = store._projectService.get_project_users();
          const response = await firstValueFrom(
            combineLatest([invites, projectUsers]).pipe(
              map(([invites, project_users]) => ({
                user_invites: {
                  invites: Array.isArray(invites[0]?.users) ? invites[0]?.users : [],
                  project_invite: Array.isArray(project_users) ? project_users : [],
                },
              }))
            )
          );
          if (response) {
            patchState(store, { user_invites: response.user_invites });
          }
        }
      },
    }),
    comparisonResource: resource<
      any,
      { serviceName: string; projectName: string; data: any }
    >({
      params: () => ({
        serviceName: store.comparisonRequest()?.serviceName || '',
        projectName: store.comparisonRequest()?.projectName || '',
        data: store.comparisonData(),
      }),
      loader: async ({ params }) => {
        try {
          return params.data || {};
        } catch (error) {
          console.error('Error fetching comparison data:', error);
          throw error;
        }
      },
    }),
  })),
  withMethods((store) => {
    const findEnv = (projectName: string, envName: string): ProjectEnv | undefined =>
      store.projects()
        ?.find((p) => p.project_name === projectName)
        ?.public___project_envs?.find((e) => e.env_name === envName);

    // Logs into an env with its stored credentials; cached briefly so an import does not re-login per click
    const getEnvAuth = async (env: ProjectEnv): Promise<InstanceAuth | null> => {
      const cached = store._instanceAuthCache.get(env.env_id);
      if (cached && Date.now() - cached.at < INSTANCE_AUTH_TTL_MS) {
        return cached.auth;
      }
      try {
        const auth = await store._configService.loginEnv(env);
        store._instanceAuthCache.set(env.env_id, { auth, at: Date.now() });
        return auth;
      } catch (error) {
        throw new Error(`Login to ${env.env_name} failed: ${errorMessage(error)}`);
      }
    };

    // Runs fn with each env's credentials attached to requests under its service URL
    const withInstanceAuth = async <T>(
      targets: { env: ProjectEnv | undefined; path: string }[],
      serviceName: string,
      projectName: string,
      fn: () => Promise<T>
    ): Promise<T> => {
      const prefixes: string[] = [];
      try {
        for (const target of targets) {
          if (!target.env) {
            throw new Error('Environment not found in project');
          }
          const auth = await getEnvAuth(target.env);
          const base = target.path.replace(/\/+$/, '');
          const prefix = serviceName === 'eru-gateway' ? `${base}/store` : `${base}/store/${projectName}`;
          store._instanceAuthRegistry.register(prefix, instanceAuthHeaders(auth));
          prefixes.push(prefix);
        }
        return await fn();
      } finally {
        prefixes.forEach((prefix) => store._instanceAuthRegistry.unregister(prefix));
      }
    };

    const fetchServiceConfig = async (serviceName: string, path: string, projectName: string): Promise<any> => {
      if (serviceName === 'eru-gateway') {
        return (await firstValueFrom(store._configService.fetchGatewayConfig(path))) || {};
      }
      if (!IMPORT_CATEGORIES[serviceName]) {
        throw new Error(`Unsupported service ${serviceName}`);
      }
      const response = await firstValueFrom(store._configService.fetchConfig(path, projectName));
      return response?.project || {};
    };

    const runCompare = async (request: ComparisonRequest, _initial: boolean) => {
      const { serviceName, projectName, clickedEnv, clickedServicePath, selectedEnv, selectedServicePath } = request;
      if (!selectedServicePath || !clickedServicePath) {
        throw new Error(`${serviceName} is not configured in both ${selectedEnv} and ${clickedEnv}`);
      }
      return withInstanceAuth(
        [
          { env: findEnv(projectName, selectedEnv), path: selectedServicePath },
          { env: findEnv(projectName, clickedEnv), path: clickedServicePath },
        ],
        serviceName,
        projectName,
        async () => {
          const sourceConfig = await fetchServiceConfig(serviceName, selectedServicePath, projectName);
          const targetConfig = await fetchServiceConfig(serviceName, clickedServicePath, projectName);
          const compareResponse = await firstValueFrom(
            serviceName === 'eru-gateway'
              ? store._projectService.compareGateway(clickedServicePath, projectName, sourceConfig)
              : store._projectService.compareEnvironment(clickedServicePath, projectName, sourceConfig)
          );
          patchState(store, {
            comparisonData: sourceConfig,
            comparisonTargetData: targetConfig,
            comparisonResult: compareResponse,
          });
          return compareResponse;
        }
      );
    };

    return {
    async fetchProjects(project_name?: string, env_name?: string) {
      // Decode any URL-encoded strings
      const decodedProjectName = project_name ? decodeURIComponent(project_name) : undefined;
      const decodedEnvName = env_name ? decodeURIComponent(env_name) : undefined;
      
      const fetchPromise = (async () => {
        const response = await firstValueFrom(
          store._projectService.fetchProjects(decodedProjectName, decodedEnvName)
        );
        let projects: Project[] = [];
    
        if (response && Array.isArray(response.projects)) {
          projects = response.projects
            .filter((project): project is Project => {
              const isValid =
                project &&
                typeof project === 'object' &&
                typeof project.project_id === 'string' &&
                typeof project.project_name === 'string' &&
                Array.isArray(project.public___project_envs) &&
                project.public___project_envs.every(
                  (env) =>
                    env &&
                    typeof env.env_id === 'string' &&
                    typeof env.env_name === 'string' &&
                    Array.isArray(env.public___project_services)
                );
              if (!isValid) {
                console.warn('Invalid project in API response:', project);
              }
              return isValid;
            })
            .map((project) => {
              // Compute service_access for each environment
              const envs = project.public___project_envs.map((env) => {
                const hasWriteAccess = env.public___project_services.some(
                  (service) => service.service_access === 'WRITE'
                );
                return {
                  ...env,
                  service_access: hasWriteAccess ? ('WRITE' as const) : ('READ' as const),
                };
              });
    
              // Compute service_access for the project
              const hasWriteAccess = envs.some((env) => env.service_access === 'WRITE');
    
              return {
                ...project,
                public___project_envs: envs,
                service_access: hasWriteAccess ? 'WRITE' : 'READ',
              };
            });
        } else {
          console.warn('Invalid or empty projects response:', response);
        }
    
        return projects;
      })();
      store._projectsFetch.current = fetchPromise;
      patchState(store, { requestParams: { project_name: decodedProjectName, env_name: decodedEnvName } });
      try {
        const projects = await fetchPromise;
        patchState(store, { projects });
      } catch (error) {
        console.error('Error loading projects:', error);
        store._snackbarService.error('Failed to fetch projects');
        patchState(store, { projects: [] });
      }
    },
      // Fix for the rejectInvitation method in project.store.ts
      async rejectInvitation(invitation: Invitations): Promise<void> {
      try {
      const payload = {
      project_id: invitation.project_id,
      user_email: invitation.user_email,
      env_id: invitation.env_id,
      service_id:invitation.service_id
      };
      
      await firstValueFrom(store._projectService.rejectUserInvite(payload));
      patchState(store, {
      user_invites: {
      ...store.user_invites(),
      invites: store.user_invites().invites?.map((inv) =>
      inv.user_email === invitation.user_email &&
      inv.project_id === invitation.project_id &&
      inv.env_id === invitation.env_id &&
      inv.service_id === invitation.service_id
      ? { ...inv, invite_status: "REJECTED" as const } // Use const assertion
      : inv
      ),
      },
      });
      store._snackbarService.success('Invitation rejected successfully');
      } catch (error) {
      console.error('Error rejecting invitation:', error);
      store._snackbarService.error('Failed to reject invitation');
      throw error;
      }
      },
      
      // And similarly for acceptInvitation
      async acceptInvitation(invitation: Invitations): Promise<void> {
      try {
      
      
      const payload = {
      project_id: invitation.project_id,
      user_email: invitation.user_email,
      env_id: invitation.env_id,
      service_id: invitation.service_id,
      };
      
      await firstValueFrom(store._projectService.acceptUserInvite(payload));
      patchState(store, {
      user_invites: {
      ...store.user_invites(),
      invites: store.user_invites().invites?.map((inv) =>
      inv.project_id === invitation.project_id &&
      inv.user_email === invitation.user_email &&
      inv.env_id === invitation.env_id &&
      inv.service_id === invitation.service_id
      ? { ...inv, invite_status: "ACCEPTED" as const } // Use const assertion
      : inv
      ),
      },
      });
      store._snackbarService.success('Invitation accepted successfully');
      } catch (error) {
      console.error('Error accepting invitation:', error);
      store._snackbarService.error('Failed to accept invitation');
      throw error;
      }
      },

      async isAdmin(payload: isAdmin): Promise<void> {
        try {
          patchState(store, { loading: true });
      
          const response = await firstValueFrom(
            store._projectService.isAdmin(payload)
          );
          console.log('isAdmin response:', response);
      
          // Update both project_invite and invites arrays
          patchState(store, {
            user_invites: {
              project_invite: store.user_invites().project_invite?.map((invite) =>
                invite.project_id === payload.project_id
                  ? {
                      ...invite,
                      user_details: invite.user_details.map((user) =>
                        user.user_id === payload.user_id
                          ? { ...user, is_admin: payload.is_admin }
                          : user
                      ),
                    }
                  : invite
              ) || [],
              invites: store.user_invites().invites?.map((inv) =>
                inv.project_id === payload.project_id &&
                inv.user_id === payload.user_id
                  ? { ...inv, is_admin: payload.is_admin }
                  : inv
              ) || [],
            },
          });
      
          store._snackbarService.success(
            `User ${payload.is_admin === 'true' ? 'promoted to' : 'demoted from'} admin successfully`
          );
        } catch (error: any) {
          console.error('isAdmin failed:', error);
          store._snackbarService.error('Failed to update user admin status');
          throw error;
        } finally {
          patchState(store, { loading: false });
        }
      },

    async create_project(payload: Project): Promise<void> {
      try {
        await firstValueFrom(
          store._projectService.create_project(payload)
        );
        store._snackbarService.success('Project created successfully');
      } catch (error: any) {
        console.error('Error saving projects:', error);
        store._snackbarService.error(`Failed to create project: ${errorMessage(error)}`);
        throw error;
      }
    },
    async create_project_environment(payload: ProjectEnvironmentPayload) {
      try {
        const response = await firstValueFrom(
          store._projectService.create_project_environment(payload)
        );
        store._snackbarService.success('Environment created successfully');
        return response;
      } catch (error) {
        console.error('Error creating environment:', error);
        store._snackbarService.error('Failed to create environment');
        throw error;
      }
    },
    async edit_project_environment(payload: ProjectEnvironmentPayload): Promise<void> {
      try {
        await firstValueFrom(
          store._projectService.edit_project_environment(payload)
        );
        store._snackbarService.success('Environment updated successfully');
      } catch (error) {
        console.error('Error updating environment:', error);
        store._snackbarService.error('Failed to update environment');
        throw error;
      }
    },
    async delete_project_environment(payload: { env_id: string }): Promise<void> {
      try {
        await firstValueFrom(
          store._projectService.delete_project_environment(payload)
        );
        store._snackbarService.success('Environment deleted successfully');
      } catch (error) {
        console.error('Error deleting environment:', error);
        store._snackbarService.error('Failed to delete environment');
        throw error;
      }
    },
    async create_project_service(payload: ProjectServicePayload): Promise<void> {
      try {
        await firstValueFrom(
          store._projectService.create_project_service(payload)
        );
        store._snackbarService.success('Service created successfully');
      } catch (error) {
        console.error('Error creating service:', error);
        store._snackbarService.error('Failed to create service');
        throw error;
      }
    },
    async edit_project_service(payload: EditProjectServicePayload): Promise<void> {
      try {
        await firstValueFrom(
          store._projectService.edit_project_service(payload)
        );
        store._snackbarService.success('Service updated successfully');
      } catch (error) {
        console.error('Error updating service:', error);
        store._snackbarService.error('Failed to update service');
        throw error;
      }
    },
    async delete_project_service(payload: { service_id: string }): Promise<void> {
      try {
        await firstValueFrom(
          store._projectService.delete_project_service(payload)
        );
        store._snackbarService.success('Service deleted successfully');
      } catch (error) {
        console.error('Error deleting service:', error);
        store._snackbarService.error('Failed to delete service');
        throw error;
      }
    },
    async get_user(payload: { user_str: string }) {
      try {
        const response = await firstValueFrom(
          store._projectService.get_user(payload)
        );
        return response;
      } catch (error) {
        console.error('Error fetching user:', error);
        store._snackbarService.error('Failed to fetch user');
        throw error;
      }
    },
    async invite_users(payload: InviteUserPayload): Promise<void> {
      try {
        await firstValueFrom(store._projectService.invite_users(payload));
        store._snackbarService.success('Users invited successfully');
      } catch (error) {
        console.error('Error inviting users:', error);
        store._snackbarService.error('Failed to invite users');
        throw error;
      }
    },
    async backupStore(envId: string, serviceId: string): Promise<void> {
      try {
        if (!envId || !serviceId) {
          throw new Error('Missing envId or serviceId');
        }
        const payload = { env_id: envId, service_id: serviceId };
        await firstValueFrom(store._projectService.backupService(payload));
        store._snackbarService.success('Committed to Git successfully');
      } catch (error: any) {
        console.error('projectStore: Backup failed:', error);
        store._snackbarService.error(`Commit failed: ${error?.error?.error || error?.message || 'Unknown error'}`);
        throw error;
      }
    },
    setSelectedRepository(repository: Repository | null): void {
      patchState(store, { selectedRepository: repository });
    },
    setSelectedEnvId(envId: string | null): void {
      patchState(store, { selectedEnvId: envId });
    },
    // envId defaults to the env chosen via navigateToRepositoryForm; navigate=false keeps the current page
    async saveRepository(repository: Repository, envId?: string | null, navigate = true): Promise<void> {
      try {
        const targetEnvId = envId || store.selectedEnvId();
        if (!targetEnvId) {
          throw new Error('No environment selected for this repository');
        }

        const payload: RepositoryPayload = {
          env_id: targetEnvId,
          doc: {
            installation_id: repository.installation_id,
            repo_type: repository.repo_type,
            repo_name: repository.repo_name,
            branch_name: repository.branch_name,
            auth_mode: repository.auth_mode,
            auto_commit: repository.auto_commit,
            auth_key: repository.auth_key,
          },
        };

        await firstValueFrom(store._projectService.saveRepository(payload));

        const currentRepos = store.repositories() || [];
        const updatedRepos = currentRepos.some((r) => r.repo_name === repository.repo_name)
          ? currentRepos.map((r) => (r.repo_name === repository.repo_name ? repository : r))
          : [...currentRepos, repository];
        patchState(store, {
          repositories: updatedRepos,
          projects: store.projects()?.map((project) => ({
            ...project,
            public___project_envs: project.public___project_envs.map((env) =>
              env.env_id === targetEnvId ? { ...env, repo_details: payload.doc } : env
            ),
          })),
        });
        store._snackbarService.success('Repository saved successfully');
        if (navigate) {
          patchState(store, { selectedRepository: null, selectedEnvId: null });
          store._router.navigateByUrl('/projects/list');
        }
      } catch (error: any) {
        console.error('saveRepository: Error saving repository:', error);
        store._snackbarService.error(`Failed to save repository: ${error?.error?.error || error?.message || 'Unknown error'}`);
        throw error;
      }
    },
    clearSelectedRepository(): void {
      patchState(store, { selectedRepository: null, selectedEnvId: null });
      try {
        sessionStorage.removeItem(PENDING_GITHUB_REPO_KEY);
      } catch {}
    },
    // The GitHub App install is a full-page redirect; keep the selection for /projects/github-callback
    rememberPendingGithubRepository(repository: Repository, envId: string): void {
      patchState(store, { selectedRepository: repository, selectedEnvId: envId });
      try {
        sessionStorage.setItem(PENDING_GITHUB_REPO_KEY, JSON.stringify({ repository, envId }));
      } catch {}
    },
    navigateToRepositoryForm(repository?: Repository, envId?: string): void {
      patchState(store, {
        selectedRepository: repository || null,
        selectedEnvId: envId || null,
      });
      store._router.navigateByUrl('/projects/repository');
    },
    // source = selectedEnv (config is read from it), target = clickedEnv (compared against / written to)
    async fetchAndCompareServiceData(
      serviceName: string,
      projectName: string,
      clickedEnv: string,
      selectedEnv: string,
      selectedServicePath: string,
      clickedServicePath: string
    ): Promise<any> {
      try {
        patchState(store, {
          comparisonRequest: {
            serviceName,
            projectName,
            clickedEnv,
            clickedServicePath,
            selectedEnv,
            selectedServicePath,
          },
        });
        const compareResponse = await runCompare(store.comparisonRequest()!, true);
        store._snackbarService.success('Service data compared successfully');
        return compareResponse;
      } catch (error: any) {
        console.error('Error in fetchAndCompareServiceData:', error);
        store._snackbarService.error(`Failed to compare service data: ${errorMessage(error)}`);
        throw error;
      }
    },
    // Copies an item from the source env onto the target env (also used to sync mismatches)
    async addNewInImport(
      serviceName: string,
      projectName: string,
      clickedEnv: string,
      clickedServicePath: string,
      category: string,
      value: string,
      payload: any
    ): Promise<void> {
      const normalizedCategory = normalizeCategory(serviceName, category);
      try {
        if (!serviceName || !projectName || !clickedEnv || !clickedServicePath || !normalizedCategory || !value) {
          throw new Error('Missing required parameters for import');
        }
        if (!IMPORT_CATEGORIES[serviceName]?.includes(normalizedCategory)) {
          throw new Error(`Category ${normalizedCategory} is not supported for service ${serviceName}`);
        }
        const request = store.comparisonRequest();
        const sourceConfig = store.comparisonData() as any;
        if (!payload && !sourceConfig) {
          throw new Error('No comparison data available for import');
        }

        let finalPayload = payload;
        let tenantId = '';
        if (!finalPayload) {
          if (serviceName === 'eru-gateway') {
            finalPayload = normalizedCategory === 'authorizers'
              ? sourceConfig.authorizers?.[value]
              : sourceConfig.listener_rules?.find((lr: RuleTypePayload) => lr.rule_name === value);
          } else if (VARIABLE_CATEGORIES[normalizedCategory]) {
            const vars = sourceConfig.variables?.[VARIABLE_CATEGORIES[normalizedCategory].configKey] || {};
            finalPayload = vars[value] ? { ...vars[value], key: value } : { key: value };
          } else if (serviceName === 'eru-ql') {
            finalPayload = lookupByName(sourceConfig.data_sources, 'db_alias', value);
          } else if (serviceName === 'eru-files') {
            if (normalizedCategory === 'storages') {
              finalPayload = lookupByName(sourceConfig.storages, 'storage_name', value);
            } else {
              const aesKey = sourceConfig.aes_keys?.[value];
              if (aesKey) {
                finalPayload = {
                  overwrite: 'true',
                  bits: String(aesKey.bits),
                  key_string: aesKey.key_string,
                } as AESKey;
              }
            }
          } else if (serviceName === 'eru-auth') {
            finalPayload = sourceConfig.auth?.[value];
          } else if (serviceName === 'eru-ai') {
            const found = findAiItem(sourceConfig, normalizedCategory, value);
            finalPayload = found?.item;
            tenantId = found?.tenantId || '';
          }
          if (!finalPayload) {
            throw new Error(`No matching data found for ${value} in ${normalizedCategory}`);
          }
        }
        if (serviceName === 'eru-ai' && !tenantId) {
          tenantId = findAiItem(sourceConfig, normalizedCategory, value)?.tenantId || '';
          if (!tenantId) {
            throw new Error(`Could not determine the tenant of ${value}`);
          }
        }

        const targetEnv = findEnv(projectName, clickedEnv);
        await withInstanceAuth([{ env: targetEnv, path: clickedServicePath }], serviceName, projectName, async () => {
          if (serviceName === 'eru-gateway') {
            if (normalizedCategory === 'authorizers') {
              await firstValueFrom(store._erugatewayService.saveAuthorizer(clickedServicePath, projectName, finalPayload));
            } else {
              await firstValueFrom(store._erugatewayService.saveListenerRule(clickedServicePath, projectName, finalPayload));
            }
          } else if (VARIABLE_CATEGORIES[normalizedCategory]) {
            await firstValueFrom(
              store._configService.saveVariables(clickedServicePath, projectName, finalPayload, VARIABLE_CATEGORIES[normalizedCategory].varType)
            );
          } else if (serviceName === 'eru-ql') {
            await firstValueFrom(store._eruqlService.saveDb(clickedServicePath, projectName, finalPayload));
          } else if (serviceName === 'eru-files') {
            if (normalizedCategory === 'storages') {
              await firstValueFrom(store._erufileService.saveStorage(clickedServicePath, projectName, finalPayload as Storages));
            } else {
              await firstValueFrom(store._erufileService.saveKey(clickedServicePath, projectName, value, finalPayload as AESKey));
            }
          } else if (serviceName === 'eru-auth') {
            await firstValueFrom(store._eruauthService.saveAuth(clickedServicePath, projectName, finalPayload));
          } else if (serviceName === 'eru-ai') {
            if (normalizedCategory === 'models') {
              await firstValueFrom(store._eruaiService.saveModel(clickedServicePath, projectName, tenantId, finalPayload));
            } else if (normalizedCategory === 'tools') {
              await firstValueFrom(store._eruaiService.saveTool(clickedServicePath, projectName, tenantId, finalPayload));
            } else {
              await firstValueFrom(store._eruaiService.saveAgent(clickedServicePath, projectName, tenantId, finalPayload));
            }
          }
        });

        if (request) {
          await runCompare(request, false);
        }
        store._snackbarService.success(`Copied ${normalizedCategory}: ${value} to ${clickedEnv}`);
      } catch (error: any) {
        console.error(`[addNewInImport] Error adding ${value} to ${normalizedCategory} in ${serviceName}:`, error);
        store._snackbarService.error(`Failed to import ${normalizedCategory}: ${value} (${errorMessage(error)})`);
        throw error;
      }
    },
    // Removes an item that exists only in the target env
    async deleteMissingInImport(
      serviceName: string,
      projectName: string,
      clickedEnv: string,
      clickedServicePath: string,
      category: string,
      value: string
    ): Promise<void> {
      const normalizedCategory = normalizeCategory(serviceName, category);
      try {
        if (!serviceName || !projectName || !clickedEnv || !clickedServicePath || !normalizedCategory || !value) {
          throw new Error('Missing required parameters for delete');
        }
        if (!IMPORT_CATEGORIES[serviceName]?.includes(normalizedCategory)) {
          throw new Error(`Category ${normalizedCategory} is not supported for service ${serviceName}`);
        }
        if (serviceName === 'eru-functions' || (serviceName === 'eru-files' && normalizedCategory === 'keys')) {
          throw new Error(`Deleting ${normalizedCategory} is not supported for ${serviceName}`);
        }
        const request = store.comparisonRequest();
        // Items missing from the source live in the target config
        const targetConfig = (store.comparisonTargetData() || {}) as any;

        let deletePayload: any = { key: value };
        let tenantId = '';
        if (serviceName === 'eru-gateway') {
          deletePayload = normalizedCategory === 'authorizers'
            ? targetConfig.authorizers?.[value] || { authorizer_name: value }
            : targetConfig.listener_rules?.find((lr: RuleTypePayload) => lr.rule_name === value) || { rule_name: value };
        } else if (serviceName === 'eru-ql' && normalizedCategory === 'data_sources') {
          deletePayload = lookupByName(targetConfig.data_sources, 'db_alias', value) || { db_alias: value };
        } else if (serviceName === 'eru-files' && normalizedCategory === 'storages') {
          deletePayload = lookupByName(targetConfig.storages, 'storage_name', value) || { storage_name: value };
        } else if (serviceName === 'eru-auth' && normalizedCategory === 'auth') {
          deletePayload = targetConfig.auth?.[value] || { auth_name: value };
        } else if (serviceName === 'eru-ai' && !VARIABLE_CATEGORIES[normalizedCategory]) {
          tenantId = findAiItem(targetConfig, normalizedCategory, value)?.tenantId || '';
          if (!tenantId) {
            throw new Error(`Could not determine the tenant of ${value}`);
          }
        }

        const targetEnv = findEnv(projectName, clickedEnv);
        await withInstanceAuth([{ env: targetEnv, path: clickedServicePath }], serviceName, projectName, async () => {
          if (VARIABLE_CATEGORIES[normalizedCategory]) {
            await firstValueFrom(
              store._configService.deleteVariables(clickedServicePath, projectName, deletePayload, VARIABLE_CATEGORIES[normalizedCategory].varType)
            );
          } else if (serviceName === 'eru-ql') {
            await firstValueFrom(store._eruqlService.deleteDb(clickedServicePath, projectName, deletePayload as DataSources));
          } else if (serviceName === 'eru-files') {
            await firstValueFrom(store._erufileService.deleteStorage(clickedServicePath, projectName, deletePayload as Storages));
          } else if (serviceName === 'eru-auth') {
            await firstValueFrom(store._eruauthService.deleteAuth(clickedServicePath, projectName, deletePayload as Auth));
          } else if (serviceName === 'eru-gateway') {
            if (normalizedCategory === 'authorizers') {
              await firstValueFrom(store._erugatewayService.deleteAuthorizer(clickedServicePath, projectName, deletePayload as Authorizer));
            } else {
              await firstValueFrom(store._erugatewayService.deleteListenerRule(clickedServicePath, projectName, deletePayload as RuleTypePayload));
            }
          } else if (serviceName === 'eru-ai') {
            if (normalizedCategory === 'models') {
              await firstValueFrom(store._eruaiService.deleteModel(clickedServicePath, projectName, tenantId, value));
            } else if (normalizedCategory === 'tools') {
              await firstValueFrom(store._eruaiService.deleteTool(clickedServicePath, projectName, tenantId, value));
            } else {
              await firstValueFrom(store._eruaiService.deleteAgent(clickedServicePath, projectName, tenantId, value));
            }
          }
        });

        if (request) {
          await runCompare(request, false);
        }
        store._snackbarService.success(`Deleted ${normalizedCategory}: ${value} from ${clickedEnv}`);
      } catch (error: any) {
        console.error(`[deleteMissingInImport] Error deleting ${value} from ${normalizedCategory} in ${serviceName}:`, error);
        store._snackbarService.error(`Failed to delete ${normalizedCategory}: ${value} (${errorMessage(error)})`);
        throw error;
      }
    },
    // Repository details of an env, or null when none is configured
    getEnvRepository(envId: string | null | undefined): Repository | null {
      if (!envId) return null;
      for (const project of store.projects() ?? []) {
        const env = project.public___project_envs?.find((e) => e.env_id === envId);
        if (env) return normalizeRepoDetails(env.repo_details);
      }
      return null;
    },
    clearComparisonData(): void {
      patchState(store, { comparisonData: null, comparisonResult: null, comparisonTargetData: null });
    },
    async create_user_invite(payload: any): Promise<void> {
      try {
        await firstValueFrom(
          store._projectService.create_user_invite(payload)
        );
        store._snackbarService.success('User invite created successfully');
      } catch (error) {
        console.error('Error creating user invite:', error);
        store._snackbarService.error('Failed to create user invite');
        throw error;
      }
    },
  };
  }),
  withHooks({
    onInit(store) {
      if (store.selectedEnvId()) return;
      try {
        const pending = JSON.parse(sessionStorage.getItem(PENDING_GITHUB_REPO_KEY) || 'null');
        if (pending?.envId && pending?.repository) {
          patchState(store, { selectedRepository: pending.repository, selectedEnvId: pending.envId });
        }
      } catch {}
    },
  })
);

const PENDING_GITHUB_REPO_KEY = 'eru_pending_github_repo';

// repo_details may arrive as an object, a JSON string or an empty value
export function normalizeRepoDetails(details: unknown): Repository | null {
  let value: any = details;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== 'object' || !value.repo_name) return null;
  return value as Repository;
}

const INSTANCE_AUTH_TTL_MS = 5 * 60 * 1000;

const VARIABLE_CATEGORIES: Record<string, { configKey: string; varType: string }> = {
  variables: { configKey: 'vars', varType: 'var' },
  env_variables: { configKey: 'env_vars', varType: 'envvar' },
  secrets: { configKey: 'secrets', varType: 'secret' },
};

// Compare categories that can be copied to / removed from the target
const IMPORT_CATEGORIES: Record<string, string[]> = {
  'eru-ql': ['data_sources', 'variables', 'env_variables', 'secrets'],
  'eru-files': ['storages', 'keys', 'variables', 'env_variables', 'secrets'],
  'eru-auth': ['auth', 'variables', 'env_variables', 'secrets'],
  'eru-functions': ['variables', 'env_variables', 'secrets'],
  'eru-gateway': ['authorizers', 'listener_rules'],
  'eru-ai': ['models', 'tools', 'agents', 'variables', 'env_variables', 'secrets'],
};

const AI_NAME_FIELDS: Record<string, string> = { models: 'model_name', tools: 'tool_name', agents: 'agent_name' };

// Maps compare-result category names (e.g. delete_model, new_authorizer) onto import categories
export function normalizeCategory(serviceName: string, category: string): string {
  const aliases: Record<string, string> =
    serviceName === 'eru-gateway'
      ? { authorizer: 'authorizers', listener_rule: 'listener_rules' }
      : serviceName === 'eru-ai'
      ? { model: 'models', tool: 'tools', agent: 'agents' }
      : {};
  return aliases[category] || category;
}

export function canImportCategory(serviceName: string, category: string): boolean {
  return !!IMPORT_CATEGORIES[serviceName]?.includes(normalizeCategory(serviceName, category));
}

// Config collections are maps keyed by name; tolerate arrays too
function lookupByName(collection: any, nameField: string, name: string): any {
  if (!collection) return undefined;
  if (Array.isArray(collection)) {
    return collection.find((item: any) => item?.[nameField] === name);
  }
  return collection[name] ?? Object.values(collection).find((item: any) => item?.[nameField] === name);
}

// eru-ai config is per tenant; returns the tenant holding the named model/tool/agent
function findAiItem(config: any, category: string, name: string): { tenantId: string; item: any } | null {
  const nameField = AI_NAME_FIELDS[category];
  if (!nameField) return null;
  for (const [key, tenant] of Object.entries<any>(config?.tenants || {})) {
    const item = lookupByName(tenant?.[category], nameField, name);
    if (item) {
      return { tenantId: tenant?.tenant_id || key, item };
    }
  }
  return null;
}

function errorMessage(error: any): string {
  return error?.error?.error || error?.message || 'Unknown error';
}

function processProjects(data: Project[]): Project[] {
  return (
    data
      .map((project) => {
        let totalServices = 0;
        if (project?.public___project_envs) {
          project.public___project_envs.forEach((env: ProjectEnv) => {
            totalServices += env.public___project_services?.length || 0;
          });
        }
        return {
          ...project,
          total_no_of_services: totalServices, // Match ProjectListComponent
        };
      })
      .sort((a, b) => a.project_name.localeCompare(b.project_name))
  );
}

export function create_id(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
